import { useCallback, useEffect, useRef } from 'react';
import { RecentOrderDto } from '../../api-types/models/OrderDto';
import { OrderStatisticsDto } from '../../api-types/models/OrderStatisticsDto';
import { RECONNECT_BACKOFF_MS, MAX_SNAPSHOT_ORDERS } from './constants';
import type { WebSocketMessage } from './types';
import { getWsToken } from '../../api/auth';

interface ObserverState {
    orders: RecentOrderDto[];
    statistics: OrderStatisticsDto | undefined;
    isKitchenOpen: boolean;
}

/**
 * Hook that manages WebSocket connection for the observer page.
 * Handles real-time order updates, statistics, and kitchen status via buffered state updates.
 */
export function useObserverWebSocket(
    token: string | undefined,
    reconnectKey: number,
    refreshIntervalMs: number,
    snapshotMode: boolean,
    onStateUpdate: (updates: Partial<ObserverState>) => void
) {
    // Buffers hold incoming data before flushing to React state
    const ordersBufferRef = useRef<RecentOrderDto[]>([]);
    const statisticsBufferRef = useRef<OrderStatisticsDto | undefined>(
        undefined
    );
    const kitchenOpenBufferRef = useRef<boolean | null>(null);
    const openingHydratedRef = useRef(false);
    const recentOrdersHydratedRef = useRef(false);
    const hasFlushedSnapshotRef = useRef(false);

    const flushBuffersToState = useCallback(() => {
        const nextOrders = ordersBufferRef.current;
        const nextStats = statisticsBufferRef.current;
        const nextKitchenOpen = kitchenOpenBufferRef.current;

        onStateUpdate({
            orders: nextOrders,
            statistics: nextStats,
            isKitchenOpen: nextKitchenOpen ?? false
        });
    }, [onStateUpdate]);

    /**
     * When opening times + recent orders are known, apply buffer immediately (live mode).
     * Snapshot mode waits for the flush interval so STATISTICS and other payloads can land first.
     */
    const maybeFlushOnHydration = useCallback(() => {
        if (!openingHydratedRef.current || !recentOrdersHydratedRef.current) {
            return;
        }
        if (snapshotMode) {
            return;
        }
        flushBuffersToState();
    }, [flushBuffersToState, snapshotMode]);

    useEffect(() => {
        if (token === undefined) {
            return;
        }

        let disposed = false;
        const intervalIds: ReturnType<typeof setInterval>[] = [];

        // Reset buffers on each connection
        ordersBufferRef.current = [];
        statisticsBufferRef.current = undefined;
        kitchenOpenBufferRef.current = null;
        openingHydratedRef.current = false;
        recentOrdersHydratedRef.current = false;

        const webSocket = new WebSocket(
            `wss://${
                process.env.REACT_APP_WEBSOCKET_DOMAIN ?? window.location.host
            }/api/ws/v2/kitchen?authToken=${token}`
        );

        const scheduleReconnect = () => {
            if (disposed) return;
            const timeoutId = setTimeout(() => {
                if (disposed) return;
                getWsToken()
                    .then(() => {
                        if (disposed) return;
                        onStateUpdate({});
                    })
                    .catch(() => {
                        if (!disposed) onStateUpdate({});
                    });
            }, RECONNECT_BACKOFF_MS);
            intervalIds.push(
                timeoutId as unknown as ReturnType<typeof setInterval>
            );
        };

        webSocket.onopen = () => {
            webSocket.send(JSON.stringify({ event: 'get_recent_orders' }));
            webSocket.send(JSON.stringify({ event: 'get_opening_times' }));
        };

        webSocket.onclose = () => {
            if (!disposed) scheduleReconnect();
        };

        webSocket.onmessage = (e) => {
            try {
                const message: WebSocketMessage = JSON.parse(e.data);

                switch (message.type) {
                    case 'RECENT_COMPLETED_ORDER':
                        ordersBufferRef.current = [
                            {
                                ...message.data.orderItem,
                                displayName: message.data.displayName
                            },
                            ...ordersBufferRef.current
                        ];
                        if (
                            snapshotMode &&
                            ordersBufferRef.current.length > MAX_SNAPSHOT_ORDERS
                        ) {
                            ordersBufferRef.current =
                                ordersBufferRef.current.slice(
                                    0,
                                    MAX_SNAPSHOT_ORDERS
                                );
                        }
                        if (
                            !snapshotMode &&
                            openingHydratedRef.current &&
                            recentOrdersHydratedRef.current
                        ) {
                            flushBuffersToState();
                        }
                        break;
                    case 'RECENT_ORDERS':
                        ordersBufferRef.current = message.data.map((order) => ({
                            ...order.orderItem,
                            displayName: order.displayName
                        }));
                        recentOrdersHydratedRef.current = true;
                        maybeFlushOnHydration();
                        break;
                    case 'STATISTICS':
                        statisticsBufferRef.current = message.data;
                        break;
                    case 'OPENING_TIMES': {
                        kitchenOpenBufferRef.current = Boolean(
                            message.data.lunch || message.data.breakfast
                        );
                        openingHydratedRef.current = true;
                        maybeFlushOnHydration();
                        break;
                    }
                }
            } catch (error) {
                console.error(
                    'Failed to parse WebSocket message:',
                    e.data,
                    error
                );
            }
        };

        webSocket.onerror = () => {
            console.error('WebSocket error');
            webSocket.close();
        };

        // Ping interval
        intervalIds.push(
            setInterval(() => {
                try {
                    if (webSocket.readyState === WebSocket.OPEN) {
                        webSocket.send(JSON.stringify({ event: 'ping' }));
                    } else if (
                        webSocket.readyState !== WebSocket.CLOSING &&
                        webSocket.readyState !== WebSocket.CLOSED
                    ) {
                        webSocket.close();
                    }
                } catch (error) {
                    if (
                        webSocket.readyState !== WebSocket.CLOSING &&
                        webSocket.readyState !== WebSocket.CLOSED
                    ) {
                        webSocket.close();
                    }
                    console.error('Ping error:', error);
                }
            }, 10000),
            // Refresh data interval
            setInterval(() => {
                if (webSocket.readyState === WebSocket.OPEN) {
                    webSocket.send(
                        JSON.stringify({ event: 'get_recent_orders' })
                    );
                    webSocket.send(
                        JSON.stringify({ event: 'get_opening_times' })
                    );
                }
            }, 60000),
            // Flush interval
            setInterval(() => {
                if (snapshotMode) {
                    if (!hasFlushedSnapshotRef.current) {
                        flushBuffersToState();
                        hasFlushedSnapshotRef.current = true;
                    }
                    return;
                }
                flushBuffersToState();
            }, refreshIntervalMs)
        );

        return () => {
            disposed = true;
            intervalIds.forEach((id) => clearInterval(id));
            webSocket.onopen = null;
            webSocket.onclose = null;
            webSocket.onmessage = null;
            webSocket.onerror = null;
            webSocket.close();
        };
    }, [
        token,
        reconnectKey,
        refreshIntervalMs,
        snapshotMode,
        flushBuffersToState,
        maybeFlushOnHydration
    ]);
}
